import { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import {
  Bars3Icon,
  ShoppingCartIcon,
  UserIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import logo from "./logo3.jpg";
import { useDispatch, useSelector } from "react-redux";
import { fetchCategoriesAction } from "../../redux/slices/categories/categoriesSlice";
import { getCartItemsFromLocalStorageAction } from "../../redux/slices/cart/cartSlice";
import { logoutAction } from "../../redux/slices/users/usersSlice";
import LanguageSwitcher from "./LanguageSwitcher";
import { useNavigate } from "react-router-dom";

export default function Navbar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Redux state
  const { userAuth } = useSelector((state) => state?.users || {});
  const userInfo = userAuth?.userInfo;
  const user = userInfo?.userFound;   // ← actual user object
  const token = userInfo?.token;
  const isLoggedIn = !!token;
  const isAdmin = user?.isAdmin === true;

  const { cartItems } = useSelector((state) => state?.carts || {});
  const cartCount = cartItems?.length || 0;

  // Fetch categories (needed for navigation, but we don't display them here)
  useEffect(() => {
    dispatch(fetchCategoriesAction());
  }, [dispatch]);

  // Load cart from localStorage
  useEffect(() => {
    dispatch(getCartItemsFromLocalStorageAction());
  }, [dispatch]);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const logoutHandler = async () => {
    await dispatch(logoutAction());
    navigate("/");
  };

  return (
    <div className="bg-white">
      {/* Mobile menu */}
      <Transition.Root show={mobileMenuOpen} as={Fragment}>
        <Dialog as="div" className="relative z-40 lg:hidden" onClose={setMobileMenuOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 z-40 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative flex w-full max-w-xs flex-col overflow-y-auto bg-white pb-12 shadow-xl">
                <div className="flex px-4 pt-5 pb-2">
                  <button
                    type="button"
                    className="-m-2 inline-flex items-center justify-center rounded-md p-2 text-gray-400"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span className="sr-only">{t("navbar.closeMenu")}</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                {/* Shopping Cart */}
                <div className="space-y-6 border-t border-gray-200 py-6 px-4">
                  <div className="flow-root">
                    <Link
                      to="/shopping-cart"
                      className="-m-2 flex items-center p-2 font-medium text-gray-900"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <ShoppingCartIcon className="h-6 w-6 flex-shrink-0 text-gray-400 mr-3" />
                      <span className="text-sm font-medium text-gray-700">
                        {t("navbar.shoppingCart")} ({cartCount})
                      </span>
                    </Link>
                  </div>
                </div>

                {/* Admin Dashboard (mobile) */}
                {isAdmin && (
                  <div className="space-y-6 border-t border-gray-200 py-6 px-4">
                    <div className="flow-root">
                      <Link
                        to="/admin"
                        className="-m-2 flex items-center p-2 font-medium text-gray-900"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="h-6 w-6 flex-shrink-0 text-red-500 mr-3"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M10.5 6h9.75M10.5 12h9.75m-9.75 6h9.75M3.75 6h.008v.008H3.75V6zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zM3.75 12h.008v.008H3.75V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm-.375 6h.008v.008H3.75V18zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
                          />
                        </svg>
                        <span className="text-sm font-medium text-gray-900">
                          {t("navbar.adminDashboard")}
                        </span>
                      </Link>
                    </div>
                  </div>
                )}

                {/* Logged-in user links (mobile) */}
                {isLoggedIn && (
                  <div className="space-y-6 border-t border-gray-200 py-6 px-4">
                    <div className="flow-root">
                      <Link
                        to="/customer-profile"
                        className="-m-2 flex items-center p-2 font-medium text-gray-900"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <UserIcon className="h-6 w-6 flex-shrink-0 text-gray-400 mr-3" />
                        <span className="text-sm font-medium text-gray-700">
                          {t("navbar.myProfile")}
                        </span>
                      </Link>
                    </div>
                    <div className="flow-root">
                      <button
                        onClick={() => {
                          logoutHandler();
                          setMobileMenuOpen(false);
                        }}
                        className="-m-2 flex items-center p-2 font-medium text-gray-900 w-full"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="h-6 w-6 flex-shrink-0 text-gray-400 mr-3"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75"
                          />
                        </svg>
                        <span className="text-sm font-medium text-gray-700">
                          {t("navbar.signOut")}
                        </span>
                      </button>
                    </div>
                  </div>
                )}

                {/* Guest links (mobile) */}
                {!isLoggedIn && (
                  <div className="space-y-6 border-t border-gray-200 py-6 px-4">
                    <div className="flow-root">
                      <Link
                        to="/register"
                        className="-m-2 block p-2 font-medium text-gray-900"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {t("navbar.createAccount")}
                      </Link>
                    </div>
                    <div className="flow-root">
                      <Link
                        to="/login"
                        className="-m-2 block p-2 font-medium text-gray-900"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {t("navbar.signIn")}
                      </Link>
                    </div>
                  </div>
                )}

                {/* Spacer */}
                <div className="flex-1" />

                {/* Instagram */}
                <div className="border-t border-gray-200 py-6 px-4">
                  <div className="flow-root">
                    <a
                      href="https://www.instagram.com/mexabeer/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="-m-2 flex items-center p-2 font-medium text-gray-900"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="h-6 w-6 flex-shrink-0 text-gray-400 mr-3"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M17.25 6.75L17.25 6.751M7.5 3.75H16.5C18.1569 3.75 19.5 5.09315 19.5 6.75V17.25C19.5 18.9069 18.1569 20.25 16.5 20.25H7.5C5.84315 20.25 4.5 18.9069 4.5 17.25V6.75C4.5 5.09315 5.84315 3.75 7.5 3.75ZM12 15.75C9.92893 15.75 8.25 14.0711 8.25 12C8.25 9.92893 9.92893 8.25 12 8.25C14.0711 8.25 15.75 9.92893 15.75 12C15.75 14.0711 14.0711 15.75 12 15.75ZM17.625 6.375C17.625 6.78921 17.2892 7.125 16.875 7.125C16.4608 7.125 16.125 6.78921 16.125 6.375C16.125 5.96079 16.4608 5.625 16.875 5.625C17.2892 5.625 17.625 5.96079 17.625 6.375Z"
                        />
                      </svg>
                      <span className="text-sm font-medium text-gray-700">
                        Instagram
                      </span>
                    </a>
                  </div>
                </div>

                {/* Language Switcher */}
                <div className="border-t border-gray-200 py-6 px-4">
                  <LanguageSwitcher isMobile={true} />
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      <header className="relative z-10">
        <nav aria-label="Top">
          {/* Top bar (desktop) */}
          {!isLoggedIn && (
            <div className="bg-gray-800">
              <div className="mx-auto flex h-10 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
                <div className="hidden lg:flex lg:flex-1 lg:items-center lg:justify-end lg:space-x-6">
                  <Link
                    to="/register"
                    className="text-sm font-medium text-white hover:text-gray-100"
                  >
                    {t("navbar.createAccount")}
                  </Link>
                  <span className="h-6 w-px bg-gray-600" aria-hidden="true" />
                  <Link
                    to="/login"
                    className="text-sm font-medium text-white hover:text-gray-100"
                  >
                    {t("navbar.signIn")}
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* Main navigation */}
          <div className="bg-black">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div className="flex h-16 items-center justify-between">
                {/* Desktop logo */}
                <div className="hidden lg:flex lg:items-center">
                  <Link to="/" className="flex-items-center">
                    <span className="sr-only">Mexa Beer</span>
                    <div className="relative h-14 overflow-hidden rounded-full border-2 border-amber-500 bg-gray-900">
                      <img className="h-14 w-auto object-contain" src={logo} alt="Mexa Beer" />
                    </div>
                  </Link>
                </div>

                {/* Mobile menu button */}
                <div className="flex flex-1 items-center lg:hidden">
                  <button
                    type="button"
                    className="-ml-2 rounded-md bg-black p-2 text-white"
                    onClick={() => setMobileMenuOpen(true)}
                  >
                    <span className="sr-only">{t("navbar.openMenu")}</span>
                    <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                {/* Centered mobile logo */}
                <Link to="/" className="lg:hidden absolute left-1/2 transform -translate-x-1/2">
                  <div className="relative h-16 w-16 overflow-hidden rounded-full border-2 border-amber-500 bg-gray-900">
                    <img className="h-22 mt- -1 w-auto" src={logo} alt="Mexa Beer" />
                  </div>
                </Link>

                {/* Desktop right section */}
                <div className="hidden lg:flex flex-1 items-center justify-end">
                  {isAdmin && (
                    <Link
                      to="/admin"
                      className="inline-flex items-center rounded-md border border-transparent bg-red-500 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 mr-4"
                    >
                      {t("navbar.adminDashboard")}
                    </Link>
                  )}

                  <div className="flex items-center lg:ml-8">
                    <div className="flex space-x-8">
                      {isLoggedIn && (
                        <div className="flex">
                          <Link to="/customer-profile" className="-m-2 p-2 mr-2 text-gray-400 hover:text-gray-500">
                            <UserIcon className="h-6 w-6" />
                          </Link>
                          <button onClick={logoutHandler}>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={1.5}
                              stroke="currentColor"
                              className="w-6 h-6 text-gray-500"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75"
                              />
                            </svg>
                          </button>
                        </div>
                      )}
                    </div>

                    <span className="mx-4 h-6 w-px bg-gray-200 lg:mx-6" aria-hidden="true" />

                    <div className="flow-root">
                      <Link to="/shopping-cart" className="group -m-2 flex items-center p-2">
                        <ShoppingCartIcon className="h-6 w-6 flex-shrink-0 text-gray-400 group-hover:text-gray-500" />
                        <span className="ml-2 text-sm font-medium text-gray-700 group-hover:text-gray-800">
                          {cartCount}
                        </span>
                      </Link>
                    </div>

                    <span className="mx-4 h-6 w-px bg-gray-200 lg:mx-6" aria-hidden="true" />

                    <div className="flow-root">
                      <a
                        href="https://www.instagram.com/mexabeer/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group -m-2 flex items-center p-2"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="h-6 w-6 flex-shrink-0 text-gray-400 group-hover:text-gray-500"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M17.25 6.75L17.25 6.751M7.5 3.75H16.5C18.1569 3.75 19.5 5.09315 19.5 6.75V17.25C19.5 18.9069 18.1569 20.25 16.5 20.25H7.5C5.84315 20.25 4.5 18.9069 4.5 17.25V6.75C4.5 5.09315 5.84315 3.75 7.5 3.75ZM12 15.75C9.92893 15.75 8.25 14.0711 8.25 12C8.25 9.92893 9.92893 8.25 12 8.25C14.0711 8.25 15.75 9.92893 15.75 12C15.75 14.0711 14.0711 15.75 12 15.75ZM17.625 6.375C17.625 6.78921 17.2892 7.125 16.875 7.125C16.4608 7.125 16.125 6.78921 16.125 6.375C16.125 5.96079 16.4608 5.625 16.875 5.625C17.2892 5.625 17.625 5.96079 17.625 6.375Z"
                          />
                        </svg>
                      </a>
                    </div>

                    <LanguageSwitcher />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </nav>
      </header>
    </div>
  );
}